import React, { useState, useRef } from 'react';
import { X, Share2, DollarSign, Clock, Move } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GamificationPopup = ({ type, onClose, onShare, onPay, onDecline, referralLink, prizeImage, gamificationStats }) => {
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  // Debug logging
  console.log('🎯 GamificationPopup props:', {
    type,
    referralLink,
    hasGamificationStats: !!gamificationStats,
    referralCode: gamificationStats?.referral_code
  });

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    setImagePosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch drag handlers for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - imagePosition.x,
      y: touch.clientY - imagePosition.y
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    setImagePosition({ x: newX, y: newY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (type === '10_likes') {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent 
          className="max-w-[90vw] sm:max-w-lg p-0 overflow-hidden max-h-[85vh] overflow-y-auto"
          style={{
            position: 'fixed',
            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={onClose}
            className="absolute top-1 right-1 sm:top-4 sm:right-4 z-10 bg-white/90 backdrop-blur-sm rounded-full p-1 sm:p-2 shadow-lg hover:bg-white"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <X className="w-3 h-3 sm:w-5 sm:h-5" />
          </button>
          
          {/* Drag indicator at top */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 bg-white/80 backdrop-blur-sm rounded-full px-2 py-1 shadow-md flex items-center space-x-1">
            <Move className="w-2 h-2 sm:w-3 sm:h-3 text-gray-700" />
            <span className="text-[8px] sm:text-[10px] text-gray-600 font-medium">Drag to move</span>
          </div>
          
          {/* Hero Image - No longer draggable separately */}
          <div className="relative h-32 sm:h-72 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 overflow-hidden">
            {prizeImage ? (
              <img 
                src={prizeImage} 
                alt="Prize" 
                className="w-full h-full object-cover pointer-events-none select-none"
                draggable="false"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white p-2 sm:p-6">
                  <div className="flex justify-center space-x-1 sm:space-x-4 mb-1 sm:mb-4">
                    <span className="text-2xl sm:text-6xl">✈️</span>
                    <span className="text-2xl sm:text-6xl">🛥️</span>
                    <span className="text-2xl sm:text-6xl">🍽️</span>
                  </div>
                  <h1 className="text-lg sm:text-4xl font-bold mb-0.5 sm:mb-2 drop-shadow-lg">WIN A DATE!</h1>
                  <p className="text-xs sm:text-xl drop-shadow-md">All Expenses Paid Trip</p>
                </div>
              </div>
            )}
            {/* Overlay for text on image */}
            {prizeImage && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent flex items-end justify-center pb-2 sm:pb-6 pointer-events-none">
                <div className="text-center text-white px-2 sm:px-4">
                  <h1 className="text-base sm:text-4xl font-bold mb-0 sm:mb-1 drop-shadow-lg">WIN A DATE!</h1>
                  <p className="text-xs sm:text-lg drop-shadow-md">Share to Unlock Prizes</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Content - More compact on mobile */}
          <div className="p-3 sm:p-6" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
            <h2 className="text-base sm:text-2xl font-bold text-center mb-2 sm:mb-4 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Share to Unlock Prizes! 🎁
            </h2>
            
            <div className="space-y-1.5 sm:space-y-3 mb-3 sm:mb-6">
              <div className="flex items-start space-x-1.5 sm:space-x-3">
                <span className="text-base sm:text-2xl">🛩️</span>
                <div>
                  <p className="font-semibold text-xs sm:text-base">Private Jet Travel</p>
                  <p className="text-[10px] sm:text-sm text-gray-600">Fly in luxury to your dream destination</p>
                </div>
              </div>
              <div className="flex items-start space-x-1.5 sm:space-x-3">
                <span className="text-base sm:text-2xl">🏖️</span>
                <div>
                  <p className="font-semibold text-xs sm:text-base">Luxury Accommodations</p>
                  <p className="text-[10px] sm:text-sm text-gray-600">5-star resorts and exclusive experiences</p>
                </div>
              </div>
              <div className="flex items-start space-x-1.5 sm:space-x-3">
                <span className="text-base sm:text-2xl">🎭</span>
                <div>
                  <p className="font-semibold text-xs sm:text-base">VIP Activities</p>
                  <p className="text-[10px] sm:text-sm text-gray-600">Exclusive tours, shows, and adventures</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔵 Share button clicked!');
                console.log('🔵 onShare function exists:', !!onShare);
                console.log('🔵 referralLink:', referralLink);
                console.log('🔵 gamificationStats:', gamificationStats);
                
                // Use share preview URL if we have referral code
                let shareUrl = referralLink;
                if (gamificationStats?.referral_code && !referralLink) {
                  // Generate share preview URL from referral code
                  shareUrl = `${BACKEND_URL}/api/share/ref/${gamificationStats.referral_code}`;
                  console.log('🔵 Generated share preview URL:', shareUrl);
                } else if (gamificationStats?.referral_code) {
                  // Replace regular URL with share preview URL for better previews
                  shareUrl = `${BACKEND_URL}/api/share/ref/${gamificationStats.referral_code}`;
                  console.log('🔵 Using share preview URL:', shareUrl);
                }
                
                // Direct Facebook share as primary method
                if (shareUrl) {
                  const shareText = `Join me on HeartLink and find your perfect match! 💕`;
                  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
                  
                  console.log('🔵 Opening Facebook directly:', facebookShareUrl);
                  
                  // Open Facebook share dialog
                  const shareWindow = window.open(facebookShareUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
                  
                  if (shareWindow) {
                    console.log('✅ Facebook share window opened!');
                    // Also call parent handler for tracking
                    if (onShare) {
                      console.log('🔵 Calling parent onShare for tracking...');
                      onShare();
                    }
                  } else {
                    console.error('❌ Pop-up blocked!');
                    alert('Pop-up blocked! Please allow pop-ups for this site to share on Facebook.');
                  }
                } else {
                  console.error('❌ No referral link available!');
                  console.error('Debug info:', { referralLink, gamificationStats, referralCode: gamificationStats?.referral_code });
                  
                  // Show user-friendly error
                  alert('Unable to generate share link. Please refresh the page and try again.\n\nIf the problem persists, contact support.');
                  
                  // Fallback to parent handler
                  if (onShare) {
                    console.log('🔵 Calling parent onShare as fallback...');
                    onShare();
                  }
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 sm:py-6 text-sm sm:text-lg font-bold shadow-lg hover:shadow-xl transition-all"
              type="button"
            >
              <Share2 className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              Share to Facebook - Unlock Prizes
            </Button>
            
            <p className="text-[10px] sm:text-xs text-center text-gray-500 mt-1.5 sm:mt-3">
              Every person who signs up through your link gets you closer to winning!
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (type === '20_likes') {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent 
          className="max-w-[90vw] sm:max-w-lg p-0 overflow-hidden max-h-[85vh] overflow-y-auto"
          style={{
            position: 'fixed',
            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={onClose}
            className="absolute top-1 right-1 sm:top-4 sm:right-4 z-10 bg-white rounded-full p-1 sm:p-2 shadow-lg hover:bg-gray-100"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <X className="w-3 h-3 sm:w-5 sm:h-5" />
          </button>
          
          {/* Drag indicator */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 bg-white/80 backdrop-blur-sm rounded-full px-2 py-1 shadow-md flex items-center space-x-1">
            <Move className="w-2 h-2 sm:w-3 sm:h-3 text-gray-700" />
            <span className="text-[8px] sm:text-[10px] text-gray-600 font-medium">Drag to move</span>
          </div>
          
          {/* Hero Image - Compact for Mobile */}
          <div className="relative h-32 sm:h-64 bg-gradient-to-br from-green-400 via-blue-500 to-purple-500">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center text-white p-2 sm:p-6">
                <div className="flex justify-center space-x-1 sm:space-x-4 mb-1 sm:mb-4">
                  <span className="text-2xl sm:text-6xl">🎊</span>
                  <span className="text-2xl sm:text-6xl">💎</span>
                  <span className="text-2xl sm:text-6xl">🏆</span>
                </div>
                <h1 className="text-lg sm:text-4xl font-bold mb-0.5 sm:mb-2">KEEP GOING!</h1>
                <p className="text-xs sm:text-xl">More Prizes Await</p>
              </div>
            </div>
          </div>
          
          {/* Content - Compact */}
          <div className="p-3 sm:p-6" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
            <h2 className="text-base sm:text-2xl font-bold text-center mb-2 sm:mb-4">You're on Fire! 🔥</h2>
            <p className="text-center text-xs sm:text-base text-gray-600 mb-3 sm:mb-6">
              Share again and get even more people to join for bigger prizes!
            </p>
            
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔵 Share button clicked (20 likes)!');
                
                // Use share preview URL
                let shareUrl = referralLink;
                if (gamificationStats?.referral_code) {
                  shareUrl = `${BACKEND_URL}/api/share/ref/${gamificationStats.referral_code}`;
                  console.log('🔵 Using share preview URL:', shareUrl);
                }
                
                // Direct Facebook share
                if (shareUrl) {
                  const shareText = `Join me on HeartLink and find your perfect match! 💕`;
                  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
                  
                  console.log('🔵 Opening Facebook:', facebookShareUrl);
                  
                  const shareWindow = window.open(facebookShareUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
                  
                  if (shareWindow) {
                    console.log('✅ Facebook opened!');
                    if (onShare) onShare();
                  } else {
                    alert('Pop-up blocked! Please allow pop-ups to share.');
                  }
                } else if (onShare) {
                  onShare();
                } else {
                  alert('Unable to share. Please try again.');
                }
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-3 sm:py-6 text-sm sm:text-lg font-bold"
              type="button"
            >
              <Share2 className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              Share Again - Get More Prizes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (type === '40_likes_payment') {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent 
          className="max-w-[90vw] sm:max-w-lg p-0 overflow-hidden max-h-[85vh] overflow-y-auto"
          style={{
            position: 'fixed',
            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={onClose}
            className="absolute top-1 right-1 sm:top-4 sm:right-4 z-10 bg-white rounded-full p-1 sm:p-2 shadow-lg hover:bg-gray-100"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <X className="w-3 h-3 sm:w-5 sm:h-5" />
          </button>
          
          {/* Drag indicator */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 bg-white/80 backdrop-blur-sm rounded-full px-2 py-1 shadow-md flex items-center space-x-1">
            <Move className="w-2 h-2 sm:w-3 sm:h-3 text-gray-700" />
            <span className="text-[8px] sm:text-[10px] text-gray-600 font-medium">Drag to move</span>
          </div>
          
          {/* Hero Image - Compact for Mobile */}
          <div className="relative h-32 sm:h-64 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center text-white p-2 sm:p-6">
                <div className="flex justify-center space-x-1 sm:space-x-4 mb-1 sm:mb-4">
                  <span className="text-2xl sm:text-6xl">💰</span>
                  <span className="text-2xl sm:text-6xl">⭐</span>
                  <span className="text-2xl sm:text-6xl">🎁</span>
                </div>
                <h1 className="text-lg sm:text-4xl font-bold mb-0.5 sm:mb-2">PREMIUM TIME!</h1>
                <p className="text-xs sm:text-xl">Continue Winning Amazing Prizes</p>
              </div>
            </div>
          </div>
          
          {/* Content - Compact */}
          <div className="p-3 sm:p-6" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
            <h2 className="text-base sm:text-2xl font-bold text-center mb-2 sm:mb-4">You've Reached 40 Likes! 🎉</h2>
            <p className="text-center text-xs sm:text-base text-gray-600 mb-3 sm:mb-6">
              To continue discovering matches and winning prizes, choose an option:
            </p>
            
            <div className="space-y-2 sm:space-y-4">
              <Button
                onClick={onPay}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white py-3 sm:py-6 text-sm sm:text-lg font-bold"
              >
                <DollarSign className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                Pay $10 USD - Continue Now
              </Button>
              
              <Button
                onClick={onDecline}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                variant="outline"
                className="w-full border-2 border-gray-300 py-3 sm:py-6 text-sm sm:text-lg"
              >
                <Clock className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                Try Back in 3 Days (Free)
              </Button>
            </div>
            
            <p className="text-[10px] sm:text-xs text-center text-gray-500 mt-2 sm:mt-4">
              Premium members get unlimited likes and exclusive benefits!
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return null;
};

export default GamificationPopup;
